import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { useAptorApi } from '../../../Api';
import { LawLink } from '../../../Components';
import { TextField } from '../../../Components/Field/TextField';
import { useConfirmation } from '../../../Context/ConfirmationContext/ConfirmationContext';
import { UserContext } from '../../../Context/UserContext/UserContext';
import { useIsMounted } from '../../../Hooks/useIsMounted';
import { ILawChange } from '../Features/OurLaws/Law/Changes';
import { IRequirementChange } from '../Features/OtherRequirements/Requirement/Changes';

const PRIMARY = '#c4122f';
const outlinedRedBtn: React.CSSProperties = { fontWeight: 700, borderColor: PRIMARY, color: PRIMARY };

export type ChangeAcknowledgeDialogTarget = {
  id: number;
  number: string;
  name?: string;
  lawId?: number;
  reqId?: number;
  type: 'law' | 'requirement';
  mode?: 'acknowledge' | 'review';
  acknowledgement?: {
    comment?: string | null;
    affectsBusiness?: boolean | null;
    acknowledgedAt?: Date;
    userName?: string;
  };
};

interface IProps {
  target?: ChangeAcknowledgeDialogTarget;
  targets?: ChangeAcknowledgeDialogTarget[];
  onClose: () => void;
  onAcknowledged: () => void;
}

type AffectsBusiness = true | false | null;

export const ChangeAcknowledgeDialog = ({ target, targets, onClose, onAcknowledged }: IProps) => {
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const { deleteConfirmation } = useConfirmation();
  const user = useContext(UserContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMounted = useIsMounted();
  const [change, setChange] = useState<ILawChange | IRequirementChange>();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [comment, setComment] = useState('');
  const [affectsBusiness, setAffectsBusiness] = useState<AffectsBusiness>(null);
  const batchTargets = targets || [];
  const isBatchMode = batchTargets.length > 0;
  const activeTarget = target || batchTargets[0];
  const isAlreadyAcknowledged =
    Boolean(change) &&
    Array.isArray((change as any).unacknowledgedByUsers) &&
    !(change as any).unacknowledgedByUsers.some((userId: number) => Number(userId) === Number(user.id));
  const isReviewMode = !isBatchMode && (target?.mode === 'review' || Boolean(target?.acknowledgement) || isAlreadyAcknowledged);

  useEffect(() => {
    if (!activeTarget) {
      setChange(undefined);
      setIsLoading(false);
      setIsSaving(false);
      setComment('');
      setAffectsBusiness(null);
      return;
    }

    if (isBatchMode) {
      setChange(undefined);
      setComment('');
      setAffectsBusiness(null);
      setIsLoading(false);
      return;
    }

    const loadChange = async () => {
      setChange(undefined);
      setComment(activeTarget.acknowledgement?.comment || '');
      setAffectsBusiness(activeTarget.acknowledgement?.affectsBusiness ?? null);
      setIsLoading(true);

      try {
        if (activeTarget.type === 'law' && activeTarget.lawId) {
          const response = await api.getLawPortalLawChanges<ILawChange>(activeTarget.lawId);
          const selectedChange = response.items.find((item) => item.id === activeTarget.id || item.number === activeTarget.number);
          if (isMounted) {
            setChange(selectedChange);
          }
        }

        if (activeTarget.type === 'requirement' && activeTarget.reqId) {
          const response = await api.getLawPortalRequirementChanges<IRequirementChange>(activeTarget.reqId);
          const selectedChange = response.items.find((item) => item.id === activeTarget.id || item.name === activeTarget.number);
          if (isMounted) {
            setChange(selectedChange);
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadChange();
  }, [api, isBatchMode, isMounted, activeTarget]);

  const closeDialog = () => {
    setChange(undefined);
    setIsLoading(false);
    setIsSaving(false);
    setComment('');
    setAffectsBusiness(null);
    onClose();
  };

  const handleCancel = () => {
    closeDialog();
  };

  // Submit one acknowledgement payload to either a single change or every selected batch target.
  const acknowledgeAction = async () => {
    if ((!activeTarget || (!isBatchMode && !change)) || isSaving) {
      return;
    }

    setIsSaving(true);
    const affectValue = affectsBusiness === null ? undefined : affectsBusiness;
    try {
      if (isBatchMode) {
        await Promise.all(
          batchTargets.map((batchTarget) => {
            if (batchTarget.type === 'law' && batchTarget.lawId) {
              return api.acknowledgeLawPortalLawChange(batchTarget.lawId, batchTarget.id, comment, affectValue);
            }

            if (batchTarget.type === 'requirement' && batchTarget.reqId) {
              return api.acknowledgeLawPortalRequirementChange(batchTarget.reqId, batchTarget.id, comment, affectValue);
            }

            return Promise.resolve();
          }),
        );
      } else if (activeTarget.type === 'law' && activeTarget.lawId && change) {
        await api.acknowledgeLawPortalLawChange(activeTarget.lawId, change.id, comment, affectValue);
      }

      if (!isBatchMode && activeTarget.type === 'requirement' && activeTarget.reqId && change) {
        await api.acknowledgeLawPortalRequirementChange(activeTarget.reqId, change.id, comment, affectValue);
      }

      onAcknowledged();
      closeDialog();
    } finally {
      if (isMounted) {
        setIsSaving(false);
      }
    }
  };

  const handleAcknowledge = () => {
    if (isReviewMode) {
      return;
    }

    deleteConfirmation(acknowledgeAction, {
      title: formatMessage({
        id: isBatchMode
          ? 'component.law-change.acknowledge.several.label'
          : activeTarget?.type === 'requirement'
            ? 'law-portal.requirements.changes.acknowledge.label'
            : 'component.law-change.acknowledge.label',
      }),
      description: formatMessage(
        {
          id: isBatchMode
            ? 'component.law-change.acknowledge.several.confirmation'
            : activeTarget?.type === 'requirement'
              ? 'law-portal.requirements.changes.acknowledge.confirmation'
              : 'component.law-change.acknowledge.confirmation',
        },
        { count: batchTargets.length },
      ),
    });
  };

  const renderChangeDetails = () => {
    if (!change) {
      return null;
    }

    const lawChange = change as ILawChange;

    return (
      <Box>
        <Typography dangerouslySetInnerHTML={{ __html: change.shortDescription }} />
        {'extent' in change && lawChange.extent && <Typography>{lawChange.extent}</Typography>}
        {'urlAddress' in change && lawChange.urlAddress && <LawLink type="law-change" url={lawChange.urlAddress} />}
        <Typography>
          {activeTarget?.type === 'requirement' ? (
            <FormattedMessage
              id="law-portal.requirements.changes.effectiveFrom"
              values={{ date: moment(change.effectiveFrom).format('ll') }}
            />
          ) : (
            <FormattedMessage
              id="component.law-change.effectiveFrom"
              values={{ date: moment(change.effectiveFrom).format('ll') }}
            />
          )}
        </Typography>
        {'hasTransitionalRegulation' in change && lawChange.hasTransitionalRegulation === true && (
          <Typography>
            <FormattedMessage id="component.law-change.has-transitional-regulation" />
          </Typography>
        )}
        {'reprint' in change && lawChange.reprint && (
          <Typography>
            <FormattedMessage id="component.law-change.reprint" values={{ reprint: lawChange.reprint }} />
          </Typography>
        )}
        {'includeInSendouts' in change && (
          <Typography>
            <FormattedMessage
              id={
                lawChange.includeInSendouts
                  ? 'component.law-change.included-in-sendouts'
                  : 'component.law-change.not-included-in-sendouts'
              }
            />
          </Typography>
        )}
      </Box>
    );
  };

  const renderAcknowledgementInfo = () => {
    if (!isReviewMode || !target?.acknowledgement) {
      return null;
    }

    return (
      <Box style={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(1) }}>
        {target.acknowledgement.userName && (
          <Typography>
            {formatMessage({ id: 'law-portal.statistics.table.user' })}: {target.acknowledgement.userName}
          </Typography>
        )}
        {target.acknowledgement.acknowledgedAt && (
          <Typography>
            {formatMessage({ id: 'law-portal.statistics.table.acknowledgedAt' })}: {moment(target.acknowledgement.acknowledgedAt).format('ll')}
          </Typography>
        )}
        {comment && (
          <Box style={{ marginTop: theme.spacing(2) }}>
            <Typography>{formatMessage({ id: 'component.law-change.acknowledge.comment' })}</Typography>
            <Typography style={{ whiteSpace: 'pre-wrap' }}>{comment}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Render the selected changes so the user can review the batch before acknowledging.
  const renderBatchSummary = () => {
    if (!isBatchMode) {
      return null;
    }

    return (
      <Box style={{ marginBottom: theme.spacing(2) }}>
        <Typography style={{ fontWeight: 700, marginBottom: theme.spacing(1) }}>
          {formatMessage({ id: 'component.law-change.acknowledge.several.selected' }, { count: batchTargets.length })}
        </Typography>
        <Box component="ul" style={{ margin: 0, paddingLeft: theme.spacing(3) }}>
          {batchTargets.map((batchTarget) => (
            <li key={`${batchTarget.type}-${batchTarget.lawId || batchTarget.reqId}-${batchTarget.id}`}>
              <Typography component="span">
                {batchTarget.name ? `${batchTarget.name} - ` : ''}{batchTarget.number}
              </Typography>
            </li>
          ))}
        </Box>
      </Box>
    );
  };

  const renderAcknowledgeForm = () => (
    <Box style={{ marginTop: theme.spacing(3) }}>
      <Box style={{ marginBottom: theme.spacing(3), display: 'flex', flexWrap: 'wrap', gap: isMobile ? '8px' : '16px' }}>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={affectsBusiness === true}
            name="affect"
            disabled={isReviewMode}
            onChange={(_, value) => setAffectsBusiness(value ? true : null)}
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
          <label>{formatMessage({ id: 'law-portal.our-law.ack.latest-change.checkbox.text.affect' })}</label>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox
            checked={affectsBusiness === false}
            name="not_affect"
            disabled={isReviewMode}
            onChange={(_, value) => setAffectsBusiness(value ? false : null)}
            inputProps={{ 'aria-label': 'primary checkbox' }}
          />
          <label>{formatMessage({ id: 'law-portal.our-law.ack.latest-change.checkbox.text.not_affect' })}</label>
        </Box>
      </Box>
    </Box>
  );

  const renderCommentForm = () => {
    const commentLabel = formatMessage({ id: 'component.law-change.acknowledge.comment' });

    if (isReviewMode) {
      return (
        <Box style={{ marginTop: theme.spacing(2), maxWidth: '640px' }}>
          <Typography>{commentLabel}</Typography>
          <Box
            style={{
              minHeight: '34px',
              padding: '8px 10px',
              border: '1px solid #bdbdbd',
              backgroundColor: '#fff',
              boxSizing: 'border-box',
              whiteSpace: 'pre-wrap',
            }}
          >
            {comment}
          </Box>
        </Box>
      );
    }

    return (
      <Box style={{ marginTop: theme.spacing(2) }}>
        <Box style={{ maxWidth: '640px' }}>
          <TextField
            key={`${activeTarget?.type}-${activeTarget?.id}-edit`}
            label={commentLabel}
            name="comment"
            errorTexts={[]}
            initialState={comment ? { value: comment, isValid: true } : undefined}
            onChange={(val) => setComment(val?.value || '')}
          />
        </Box>
      </Box>
    );
  };

  const isAcknowledgeDisabled = (!isBatchMode && !change) || isLoading || isSaving || (isBatchMode && batchTargets.length === 0);

  const renderActionButton = () => {
    return (
      <Button
        variant="outlined"
        onClick={handleAcknowledge}
        disabled={isAcknowledgeDisabled}
        style={{
          ...outlinedRedBtn,
          color: isAcknowledgeDisabled ? '#999' : PRIMARY,
          minWidth: '140px',
        }}
      >
        {formatMessage({
          id: isBatchMode
            ? 'component.law-change.acknowledge.several.button'
            : activeTarget?.type === 'requirement'
              ? 'law-portal.requirements.changes.acknowledge.button'
              : 'component.law-change.acknowledge.button',
        })}
      </Button>
    );
  };

  return (
    <Dialog open={Boolean(activeTarget)} maxWidth="lg" fullWidth onClose={handleCancel}>
      <DialogTitle style={{ borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6">
          {isBatchMode ? formatMessage({ id: 'component.law-change.acknowledge.several.label' }) : activeTarget?.number}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: isMobile ? theme.spacing(2) : theme.spacing(3), position: 'relative' }}>
        <Box style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outlined" onClick={handleCancel} style={outlinedRedBtn}>
            {formatMessage({ id: 'form.cancel' })}
          </Button>
        </Box>

        <Box style={{ minHeight: '420px', display: 'flex', flexDirection: 'column' }}>
          {(isLoading || isSaving) && (
            <Box style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10 }}>
              <CircularProgress />
            </Box>
          )}

          <Box
            style={{
              width: '100%',
              padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
              backgroundColor: '#f7f0f1',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minHeight: '360px',
              maxHeight: isMobile ? '68vh' : '62vh',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              style={{
                width: '100%',
                maxHeight: isMobile ? '64vh' : '58vh',
                overflow: 'auto',
                backgroundColor: '#f7f0f1',
                padding: isMobile ? `${theme.spacing(1)}px ${theme.spacing(2)}px` : `${theme.spacing(1)}px ${theme.spacing(3)}px`,
                boxSizing: 'border-box',
              }}
            >
              <Box style={{ maxWidth: '960px', width: '100%' }}>
                {renderBatchSummary()}
                {renderChangeDetails()}
                {renderAcknowledgementInfo()}
                {!isReviewMode && renderAcknowledgeForm()}
                {!isReviewMode && renderCommentForm()}
              </Box>
            </Box>
          </Box>

          <Box style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            {!isReviewMode && (isSaving ? <CircularProgress size={20} /> : renderActionButton())}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
